import { useEffect, useRef, useState } from "react";
import { CONTACT_ADDING_TYPE } from "../constants";

export const useTableHooks = (
    data,
    SortableColumns,
    contactsList = [],
    setContactsList = () => { },
    uploadedCsvData = [],
    setUploadedCsvData = () => { },
    manuallyAddedContacts = [],
    setManuallyAddedContacts = () => { }
) => {
    const [tableData, setTableData] = useState([]);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [selectAllItem, setSelectAllItem] = useState(false);
    const [selectedItem, setSelectedItem] = useState([]);
    const [dbDataToRemove, setDbDataToRemove] = useState([]); // for data from DB
    const [csvDataToRemove, setCsvDataToRemove] = useState([]);
    const [manuallyAddedDataToRemove, setManuallyAddedDataToRemove] = useState([]);
    const [tableErrors, setTableErrors] = useState({});
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
    const shiftPressedRef = useRef(false);

    useEffect(() => {
        // checking duplicates
        const emailCount = {};
        data.forEach(r => {
            emailCount[r.email] = (emailCount[r.email] || 0) + 1;
        });

        const rowsWithDuplicate = data.map(r => ({
            ...r,
            isDuplicate: emailCount[r.email] > 1
        }));
        setTableData(rowsWithDuplicate);
    }, [data]);

    const onSearchItem = (e) => {
        const value = e.target.value;
        if (!value) return setTableData(data);
        setTableData(
            data.filter((item) => item?.name?.toLowerCase().includes(value.toLowerCase()))
        );
    };

    const handleSortColumn = (columnIndex) => {
        if (!tableData.length) return;

        const columnNames = Object.keys(SortableColumns);
        let fieldName;
        fieldName = columnNames[columnIndex];

        const newSortDirection =
            sortColumn === columnIndex && sortDirection === "asc" ? "desc" : "asc";
        setSortColumn(columnIndex);
        setSortDirection(newSortDirection);

        const sortedData = [...tableData].sort((a, b) => {
            const valueA = a[fieldName] ?? "";
            const valueB = b[fieldName] ?? "";
            const comparison = valueA.localeCompare(valueB);
            return newSortDirection === "asc" ? comparison : -comparison;
        });

        setTableData(sortedData);
    };

    const handleMainCheckbox = () => {
        setSelectAllItem(!selectAllItem);
    };

    useEffect(() => {
        if (selectAllItem) {
            setSelectedItem(tableData.map((_, index) => index));
            setDbDataToRemove(tableData.filter(contact => contact.uuid).map((contact) => contact.uuid));
            setCsvDataToRemove(
                tableData.reduce((indexes, contact, index) => {
                    if (contact.type === CONTACT_ADDING_TYPE.CSV) {
                        indexes.push(index - contactsList.length);
                    }
                    return indexes;
                }, [])
            );

            setManuallyAddedDataToRemove(
                tableData.reduce((indexes, contact, index) => {
                    if (contact.type === CONTACT_ADDING_TYPE.MANUALLY_ADDED) {
                        indexes.push(index - contactsList.length - uploadedCsvData.length);
                    }
                    return indexes;
                }, [])
            );
        } else {
            setSelectedItem([]);
            setDbDataToRemove([]);
            setCsvDataToRemove([]);
            setManuallyAddedDataToRemove([]);
        }
    }, [selectAllItem]);

    const applyRowSelection = (index, forceSelect = false) => {
        const row = tableData[index];
        const isSelected = selectedItem.includes(index);

        const shouldSelect = forceSelect || !isSelected;

        // DB
        if (row?.uuid) {
            setDbDataToRemove((prev) =>
                shouldSelect
                    ? [...new Set([...prev, row.uuid])]
                    : prev.filter((i) => i !== row.uuid)
            );
        }

        // CSV
        if (row?.type === CONTACT_ADDING_TYPE.CSV) {
            const csvIndex = index - contactsList.length;
            setCsvDataToRemove((prev) =>
                shouldSelect
                    ? [...new Set([...prev, csvIndex])]
                    : prev.filter((i) => i !== csvIndex)
            );
        }

        // MANUAL
        if (row?.type === CONTACT_ADDING_TYPE.MANUALLY_ADDED) {
            const manualIndex = index - contactsList.length - uploadedCsvData.length;
            setManuallyAddedDataToRemove((prev) =>
                shouldSelect
                    ? [...new Set([...prev, manualIndex])]
                    : prev.filter((i) => i !== manualIndex)
            );
        }

        // Selected rows
        setSelectedItem((prev) =>
            shouldSelect ? [...new Set([...prev, index])] : prev.filter((i) => i !== index)
        );
    };

    const toggleItemSelection = (index) => {
        const isShift = shiftPressedRef.current;

        if (isShift && lastSelectedIndex !== null) {
            const start = Math.min(lastSelectedIndex, index);
            const end = Math.max(lastSelectedIndex, index);

            const range = [];
            for (let i = start; i <= end; i++) range.push(i);

            setSelectedItem((prev) => {
                const newSet = new Set(prev);
                range.forEach((i) => newSet.add(i));
                return [...newSet];
            });

            range.forEach((i) => applyRowSelection(i, true));
        } else {
            // Normal single toggle
            applyRowSelection(index, false);
        }

        setLastSelectedIndex(index);
    };

    const isItemSelected = (index) => selectedItem.some((u) => u === index);

    const handleDeleteSelectedItems = async () => {
        setTableData((prevData) => prevData.filter((_, index) => !selectedItem.includes(index)));
        setSelectedItem([]);
        setSelectAllItem(false);

        // If the selected data are from the database
        if (dbDataToRemove.length > 0) {
            setContactsList(contactsList.filter(obj => !dbDataToRemove.includes(obj.uuid)));
            setDbDataToRemove([]);
        }

        // If the selected data are from an uploaded CSV
        if (csvDataToRemove.length > 0) {
            setUploadedCsvData(uploadedCsvData.filter((_, index) => !csvDataToRemove.includes(index)));
            setCsvDataToRemove([]);
        }

        // If the selected data are manually added
        if (manuallyAddedDataToRemove.length > 0) {
            setManuallyAddedContacts(manuallyAddedContacts.filter(obj => !manuallyAddedDataToRemove.includes(obj.email)));
            setManuallyAddedDataToRemove([]);
        }
    };

    const updateTableField = (rowIndex, field, value) => {
        setTableData(prev => {
            const updated = prev.map((row, i) =>
                i === rowIndex ? { ...row, [field]: value } : row
            );
            return updated;
        });

        setTableErrors(prev => {
            const copy = { ...prev };
            if (copy[rowIndex]) {
                delete copy[rowIndex][field];
                if (Object.keys(copy[rowIndex]).length === 0) {
                    delete copy[rowIndex];
                }
            }
            return copy;
        });
    };

    const validateTable = () => {
        const newErrors = {};

        tableData.forEach((row, index) => {
            const rowErrors = {};

            if (!row.firstName?.trim()) rowErrors.firstName = "First name is required";
            if (!row.lastName?.trim()) rowErrors.lastName = "Last name is required";
            if (!row.email?.trim()) rowErrors.email = "Email is required";
            if (row.email && !/^\S+@\S+\.\S+$/.test(row.email)) {
                rowErrors.email = "Invalid email format";
            }

            if (Object.keys(rowErrors).length > 0) {
                newErrors[index] = rowErrors;
            }
        });

        setTableErrors(newErrors);
        return newErrors;
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Shift") shiftPressedRef.current = true;
        };
        const onKeyUp = (e) => {
            if (e.key === "Shift") shiftPressedRef.current = false;
        };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);


    return {
        tableData,
        sortColumn,
        sortDirection,
        selectedItem,
        selectAllItem,
        onSearchItem,
        handleMainCheckbox,
        toggleItemSelection,
        isItemSelected,
        setTableData,
        setSortColumn,
        setSortDirection,
        handleSortColumn,
        handleDeleteSelectedItems,
        updateTableField,
        validateTable,
        tableErrors
    };
};
